import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import {
  Table, TableHeader, TableHeaderCell, TableCell, TableRow, TableBody,
  Popover, PopoverTrigger, PopoverSurface,
  useTableFeatures, useTableSort, TableColumnId,
  Tooltip,
} from '@fluentui/react-components';

import { columns } from './columns';
import { AssetPreview } from './asset-preview';
import { useGetPerformanceTag } from '../utils/use-get-performance-tag';
import { useGetStyledAssetStatus } from '../utils/use-get-styled-asset-status';
import { getPageAssets } from '../utils/get-page-assets';
import { AssetPerformancePopover } from '../popover/asset-performance-popover';
import { useAssetTableStyles } from '../styles/asset-table-styles';
import { AssetTableProps } from '../utils/types';

const MIN_WITH_ASPECT_RATIO_WIDTH = 240;


export function AssetTable({
  i18n,
  assets,
  paginationConfig,
  assetsPerPage,
  resetToFirstPage,
  isSmallScreen,
}: AssetTableProps) {
  const styles = useAssetTableStyles();

  const getPerformanceTag = useGetPerformanceTag(i18n);
  const getStyledAssetStatus = useGetStyledAssetStatus(i18n);

  const [withAspectRatio, setWithAspectRatio] = useState(false);
  const setWithAspectRatioValue = useCallback((e: any) => {
    if (e?.offsetWidth) {
      setWithAspectRatio(e.offsetWidth > MIN_WITH_ASPECT_RATIO_WIDTH);
    }
  }, []);

  const {
    getRows,
    sort: { getSortDirection, toggleColumnSort, sort },
  } = useTableFeatures(
    {
      columns,
      items: assets,
    },
    [
      useTableSort({}),
    ]
  );

  const headerSortProps = (columnId: TableColumnId) => ({
    onClick: (e: React.MouseEvent) => {
      resetToFirstPage();
      toggleColumnSort(e, columnId);
    },
    sortDirection: getSortDirection(columnId),
  });

  const rows = sort(getRows());

  const rowsToDisplay = getPageAssets(rows, paginationConfig, assetsPerPage);
  return (
    <Table sortable className="asset-perf-table" arial-label={i18n.getString(_TL_('Asset performance table'))}>
      <TableHeader>
        <TableRow className={styles.headerRow}>
          <TableHeaderCell className="asset-header" ref={e => setWithAspectRatioValue(e)} key="asset">
            {i18n.getString(_TL_('Asset'))}
          </TableHeaderCell>
          {!isSmallScreen &&
            <React.Fragment>
              <TableHeaderCell className={classNames('performance-header', styles.performanceHeader)} key="performance" {...headerSortProps('performance')}>
                <Tooltip content={i18n.getString(_TL_('Performance'))} relationship="description">
                  <span className={styles.headerText}>
                    {i18n.getString(_TL_('Performance'))}
                  </span>
                </Tooltip>
              </TableHeaderCell>
              <TableHeaderCell className={classNames('policy-status-header', styles.policyStatusHeader)} key="policy-status" {...headerSortProps('policy-status')}>
                <Tooltip content={i18n.getString(_TL_('Policy status'))} relationship="description">
                  <span className={styles.headerText}>
                    {i18n.getString(_TL_('Policy status'))}
                  </span>
                </Tooltip>
              </TableHeaderCell>
            </React.Fragment>
          }
        </TableRow>
      </TableHeader>
      <TableBody>
        {rowsToDisplay.map(({ item }) => (
          <Popover openOnHover mouseLeaveDelay={0} key={`${item.Id}${item.AssociationType}`}>
            <PopoverTrigger>
              <TableRow className="asset-row">
                <TableCell>
                  <AssetPreview asset={item} i18n={i18n} withAspectRatio={withAspectRatio} />
                </TableCell>
                {!isSmallScreen &&
                  <React.Fragment>
                    <TableCell className={styles.performanceCell}>{getPerformanceTag(item)}</TableCell>
                    <TableCell>{getStyledAssetStatus(item)}</TableCell>
                  </React.Fragment>
                }
              </TableRow>
            </PopoverTrigger>
            <PopoverSurface>
              <AssetPerformancePopover i18n={i18n} asset={item} />
            </PopoverSurface>
          </Popover>
        ))}
      </TableBody>
    </Table>
  );
}
